import { CAMERA_SOURCE, fitFromRef, isSourceFill, sourceFill } from '~/lib/sourceFill'
import { HEIGHT, WIDTH } from '~/lib/stage'

const isPlainObject = v => !!v && typeof v === 'object' && !Array.isArray(v)

const BAG_KEYS = ['zoom', 'panX', 'panY', 'paused']
const RECT_KEYS = ['id', 'parentId', 'visible', 'opacity', 'locked', 'zIndex', 'mask', 'maskInverse', 'blendMode']
const POSE_KEYS = ['rotation', 'pivotX', 'pivotY']

const bagOf = video => {
	const bag = {}
	for (const k of BAG_KEYS) if (k in video) bag[k] = video[k]
	if (video.scaleX < 0) bag.mirror = true
	const own = video.source_settings?.[video.source]
	return isPlainObject(own) ? { ...own, ...bag } : bag
}

const rectOf = (video, source) => {
	const rect = { type: 'rect', name: video.name || 'Video', fill: sourceFill(source) }
	for (const k of RECT_KEYS) if (k in video) rect[k] = video[k]
	if (video.fit_to_frame !== false)
		return { ...rect, x: Math.round(WIDTH / 2), y: Math.round(HEIGHT / 2), width: WIDTH, height: HEIGHT }

	for (const k of POSE_KEYS) if (k in video) rect[k] = video[k]
	return {
		...rect,
		x: video.x ?? Math.round(WIDTH / 2),
		y: video.y ?? Math.round(HEIGHT / 2),
		width: Math.abs((video.width ?? WIDTH) * (video.scaleX ?? 1)),
		height: Math.abs((video.height ?? HEIGHT) * (video.scaleY ?? 1)),
	}
}

// A video node becomes source fills: on the shapes its `surfaces` reference, else on a rect in its place. Its picture settings land in the preset's `source_settings`, keyed by source; venue-surface references are dropped.
export function videoToFill(settings) {
	const objects = settings?.objects
	if (!isPlainObject(objects)) return settings
	const videos = Object.entries(objects).filter(([, o]) => o?.type === 'video')
	if (!videos.length) return settings

	const next = { ...objects }
	const bags = isPlainObject(settings.source_settings) ? { ...settings.source_settings } : {}
	const removed = new Set()

	for (const [id, video] of videos) {
		const source = video.source || CAMERA_SOURCE
		const bag = { ...bagOf(video), ...bags[source] }
		if (Object.keys(bag).length) bags[source] = bag

		const refs = Array.isArray(video.surfaces) ? video.surfaces : []
		if (!refs.length) {
			next[id] = rectOf(video, source)
			continue
		}
		delete next[id]
		removed.add(id)
		for (const ref of refs) {
			const shape = ref?.kind === 'object' ? next[ref.id] : undefined
			if (!shape || isSourceFill(shape.fill)) continue
			const filled = { ...shape, fill: sourceFill(source, fitFromRef(ref)) }
			if (filled.role === 'region') {
				delete filled.role
				delete filled.visible
			}
			next[ref.id] = filled
		}
	}

	if (removed.size)
		for (const [id, obj] of Object.entries(next))
			if (Array.isArray(obj?.children) && obj.children.some(c => removed.has(c)))
				next[id] = { ...obj, children: obj.children.filter(c => !removed.has(c)) }

	const out = { ...settings, objects: next }
	if (Object.keys(bags).length) out.source_settings = bags
	return out
}

export default videoToFill
